<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { storeToRefs } from 'pinia'

  import { IbexaColorPair } from '@/enums/IbexaColorPair'
  import { useAuthStore } from '@/stores/auth'
  import { useSubject } from '@/composables/useSubject'

  import { KsCallout } from '@aschehoug/kloss'
  import AppLayout from '@/components/layout/AppLayout.vue'
  import Credits from '@/components/Credits.vue'
  import NavigateBackwardsLink from '@/components/layout/NavigateBackwardsLink.vue'
  import SubjectPageSkeleton from '@/components/skeletons/SubjectPageSkeleton.vue'
  import StandardLayout from '@/components/layout/Layout.vue'
  import SubjectHero from '@/components/SubjectHero.vue'
  import TopicListItem from '@/components/TopicListItem.vue'
  import Typography from '@/components/common/Typography.vue'
  import SubjectTeacherButtons from '../SubjectTeacherButtons.vue'

  const { t } = useI18n()
  const { isTeacher } = storeToRefs(useAuthStore())

  const {
    comingSoonTopicList,
    credits,
    isTopicsAndCreditsLoading,
    subject,
    subjectGuide,
    topicList,
    annualPlan
  } = useSubject()
</script>

<template>
  <StandardLayout :color-pair="subject?.colorPair">
    <NavigateBackwardsLink button-text="Forsiden" />
    <SubjectHero
      v-if="subject && !isTopicsAndCreditsLoading"
      :subject="subject"
    >
      <template #buttons>
        <SubjectTeacherButtons
          :is-teacher="!!isTeacher"
          :subject-guide="subjectGuide"
          :annual-plan="annualPlan"
        />
      </template>
    </SubjectHero>
    <AppLayout>
      <SubjectPageSkeleton v-if="isTopicsAndCreditsLoading" />
      <div
        v-if="topicList"
        class="topic-container"
      >
        <KsCallout
          v-if="!topicList"
          class="callout"
          variant="info"
          :title="t('callouts.not-found-sub-elements')"
        />

        <div class="topic-list-container">
          <Typography variant="h2">
            {{ t('subjectPage.subject') }}
          </Typography>
          <ol class="topic-list">
            <TopicListItem
              v-for="(topicItem, index) in topicList"
              :key="index"
              :item="topicItem"
              :index="index"
              :color-pair="subject?.colorPair"
            />
          </ol>
        </div>
        <div
          v-if="isTeacher && comingSoonTopicList?.length"
          class="topic-list-container"
        >
          <Typography variant="h2">
            {{ t('comingSoon') }}
          </Typography>
          <ol class="topic-list">
            <TopicListItem
              v-for="(topicItem, index) in comingSoonTopicList"
              :key="index"
              :item="topicItem"
              :index="index"
              :color-pair="IbexaColorPair.DisabledPair"
              :is-disabled="true"
            />
          </ol>
        </div>
      </div>
    </AppLayout>
    <div
      v-if="credits"
      class="credits-container"
      :class="subject?.colorPair"
    >
      <Credits :credits="credits" />
    </div>
  </StandardLayout>
</template>

<style scoped lang="scss">
  @include color-pairs;

  .callout {
    margin-top: 20px;
  }

  .topic-container {
    margin-bottom: 80px;
  }

  .topic-list-container {
    margin: 80px 0 0;
  }

  .topic-list {
    list-style-type: none;
    margin: 0;
    padding: 0;
  }

  .credits-container {
    padding-top: 120px;
    color: var(--color-pair-background-color);
    background-color: var(--color-pair-color);
  }
</style>
